import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { ChapterRail, ChapterNav } from "@/components/WorkNav";
import { Reveal } from "../components/Reveal";
import { Logo } from "../components/Logo";

const AT = "/work/at20";
const PURPLE = "#5b1d6e";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] px-5 sm:px-8 h-16 flex items-center justify-between">
      <Logo imgClassName="h-9 sm:h-10" />
      <div className="flex items-center gap-4 sm:gap-6">
        <Link to="/work" data-testid="at20-back-work-top" className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">← Work</Link>
        <Link to="/#gateways" data-testid="at20-back-explore-top" className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors hidden sm:inline">Explore</Link>
      </div>
    </div>
  </header>
);

const Eyebrow = ({ children }) => (
  <span className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#e62429] font-bold">{children}</span>
);

const ARCHIVE = ["arch-2.jpg","arch-3.jpg","arch-4.jpg","arch-7.jpg","arch-8.jpg","arch-9.jpg","arch-10.jpg"];
const S12_FACTS = [
  ["10", "Teams — Mumbai / Pune"],
  ["32", "Matches"],
  ["220", "Players"],
  ["T20", "Twenty20 format"],
];

export default function At20() {
  const { scrollYProgress } = useScroll();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="at20-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-[2px] bg-[#e62429] origin-left z-[60]" />
      <Header />
      <ChapterRail currentKey="at20" />

      {/* OPENER / BEGINNING */}
      <section data-testid="at20-opener" className="min-h-[90svh] flex items-end border-b border-white/10">
        <div className="mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-36">
          <Reveal>
            <Eyebrow>Work · Client 04</Eyebrow>
            <div className="mt-5 bg-white inline-flex p-4 sm:p-5">
              <img src={`${AT}/at20-logo.jpg`} alt="AT20 — Architect's T20 Championship Trophy" className="h-16 sm:h-24 w-auto object-contain" />
            </div>
            <h1 className="mt-7 font-display uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tight">AT20</h1>
            <p className="mt-6 text-lg sm:text-2xl text-neutral-300 max-w-2xl">It started with cricket.</p>
            <p className="mt-3 text-base sm:text-lg text-neutral-400 max-w-2xl">Edifice already fielded teams in corporate cricket. The thought was simple —</p>
            <p className="mt-5 font-display uppercase text-white text-2xl sm:text-4xl leading-tight max-w-3xl">
              A serious cricket tournament for architects — <span className="text-[#e62429]">of their own.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* SEASON 1 — TEST BED */}
      <section data-testid="at20-testbed" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Season 1 · Test bed</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95] max-w-4xl">
              The ambition cost more than entry fees could fund.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
            <Reveal><div className="border border-white/12 p-8"><p className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold">Players</p><p className="mt-3 font-display uppercase text-white text-2xl sm:text-3xl">Architects + Designers</p></div></Reveal>
            <Reveal delay={0.08}><div className="border border-white/12 p-8"><p className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-bold">Brands</p><p className="mt-3 font-display uppercase text-white text-2xl sm:text-3xl">Supplying the industry</p></div></Reveal>
          </div>
          <Reveal className="mt-8">
            <p className="text-lg sm:text-2xl text-neutral-300 max-w-3xl">Could the industry that serves architects help fund the cricket architects wanted to play?</p>
          </Reveal>
        </div>
      </section>

      {/* SEASON 2 — SYSTEM */}
      <section data-testid="at20-system" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Season 2 · The test bed becomes a system</Eyebrow>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-white/12">
            {[["Sponsorship","Formalised"],["Standards","Raised"],["Entry","Limited"]].map(([a,b],i) => (
              <Reveal key={a} delay={i*0.08}>
                <div className="border-b md:border-b-0 md:border-r border-white/12 p-8 sm:p-10 h-full last:border-r-0">
                  <p className="font-display uppercase text-white text-3xl sm:text-4xl">{a}</p>
                  <p className="mt-2 text-[#e62429] uppercase tracking-[0.2em] text-sm font-bold">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8"><p className="font-display uppercase text-neutral-300 text-2xl sm:text-3xl">A place in the tournament begins to mean something.</p></Reveal>
        </div>
      </section>

      {/* RESTRICTED ENTRY */}
      <section data-testid="at20-entry" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <Eyebrow>Restricted entry</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">Getting in becomes part of the prize.</h2>
          </Reveal>
          <Reveal delay={0.1} className="flex items-center">
            <p className="text-lg sm:text-xl text-neutral-300 max-w-lg">Selective, invitation-led entry — for strong representation of the architectural community and a credible value proposition for sponsors.</p>
          </Reveal>
        </div>
      </section>

      {/* EXCLUSIVITY PROBLEM / FORMAT */}
      <section data-testid="at20-format" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>But exclusivity creates another problem</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">
              This isn't box cricket. <span className="text-[#e62429]">It's leather-ball T20.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-neutral-400 max-w-2xl">
              Even large practices may not have enough accomplished leather-ball players to field a competitive side. So the format allows —
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl">
            <Reveal><div className="border border-white/12 p-8 h-full"><p className="font-display uppercase text-white text-2xl sm:text-3xl">Single corporate teams</p></div></Reveal>
            <Reveal delay={0.08}><div className="border border-[#e62429] p-8 h-full bg-[#e62429]/[0.06]"><p className="font-display uppercase text-white text-2xl sm:text-3xl">Combination / Fusion teams</p><p className="mt-3 text-neutral-300">Architects from mid-sized and smaller firms.</p></div></Reveal>
          </div>
        </div>
      </section>

      {/* PLAYER EQUATION */}
      <section data-testid="at20-equation" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal><Eyebrow>The recurring design problem · The player equation</Eyebrow></Reveal>
          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            {["Industry relevance","Cricketing ability","Representation"].map((t,i) => (
              <Reveal key={t} delay={i*0.08}>
                <div className="flex items-center gap-4 sm:gap-6">
                  <span className="font-display uppercase text-white text-3xl sm:text-5xl leading-none">{t}</span>
                  {i < 2 && <span className="text-[#e62429] text-3xl sm:text-4xl font-display">×</span>}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8"><p className="text-lg sm:text-xl text-neutral-400 max-w-2xl">Eligibility and team-composition rules keep moving to hold the balance.</p></Reveal>
        </div>
      </section>

      {/* WHO PAYS — SPONSOR MODEL */}
      <section data-testid="at20-model" className="border-b border-white/10">
        <div className="py-24 sm:py-32" style={{ backgroundColor: PURPLE }}>
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.35em] text-white/70 font-bold">Who pays for the difference?</p>
              <h2 className="mt-6 font-display uppercase text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
                Brands supplying the architecture + interiors industry fund the environment the community wants to play in.
              </h2>
              <p className="mt-8 font-display uppercase text-white/90 text-xl sm:text-2xl">Not a bolt-on sponsor layer. Part of the model from the test bed onward.</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AUDIENCE / SALES */}
      <section data-testid="at20-audience" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow>The audience is right there. The sales pitch isn't.</Eyebrow>
            <div className="mt-8 space-y-3">
              {["Repeated contact","Informal environment","Industry peers"].map((t) => (
                <p key={t} className="font-display uppercase text-neutral-200 text-2xl sm:text-3xl border-b border-white/[0.07] pb-3">{t}</p>
              ))}
            </div>
            <p className="mt-8 text-base sm:text-lg text-neutral-400 max-w-lg">
              No promise of deals signed at the boundary line — sponsor presence in a setting that does not expect a sale, with the right audience genuinely present on and off the field.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <img src={`${AT}/presentation.jpg`} alt="AT20 presentation against the sponsor backdrop" loading="lazy" className="w-full h-auto object-contain border border-white/10" />
          </Reveal>
        </div>
      </section>

      {/* SPONSORS x SEASONS */}
      <section data-testid="at20-sponsors" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Sponsors × Seasons</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">The recurrence and turnover of names across seasons is the story.</h2>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-neutral-400 text-sm uppercase tracking-[0.2em]">
              <span>Platinum / Gold — tiers only</span><span className="text-neutral-700">·</span>
              <span>Commercial values omitted</span>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["arch-8.jpg","arch-9.jpg","arch-10.jpg","stumps.jpg"].map((f,i) => (
              <Reveal key={f} delay={i*0.06}>
                <img src={`${AT}/${f}`} alt="AT20 sponsor evidence across seasons" loading="lazy" className="w-full aspect-[4/3] object-cover border border-white/10" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHIVE */}
      <section data-testid="at20-archive" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Archive</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-none">AT20 through the years.</h2>
          </Reveal>
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {ARCHIVE.map((f, i) => (
              <Reveal key={f} delay={(i % 3) * 0.06}>
                <img src={`${AT}/${f}`} alt="AT20 season, archival" loading="lazy" className={`w-full h-auto mb-5 border border-white/10 ${i % 2 ? "rotate-[0.6deg]" : "-rotate-[0.6deg]"}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ANNUAL RITUAL / INTERRUPTIONS */}
      <section data-testid="at20-ritual" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>The annual ritual</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">Twelve seasons. Two interruptions.</h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-2">
            {Array.from({ length: 12 }, (_, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <span className="inline-flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 border border-white/15 font-display text-lg sm:text-xl text-neutral-300">
                  {i + 1}
                </span>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              "COVID resulted in the loss of two full seasons.",
              "Oneworld lost the rights to host one season.",
              "Oneworld regained the rights to AT20.",
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border-l-2 border-[#e62429] pl-5">
                  <p className="text-lg sm:text-xl text-neutral-200">{t}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEASON 12 */}
      <section data-testid="at20-season12" className="py-20 sm:py-28 border-b border-white/10">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow>Season 12 · January 2026 → March / April 2026</Eyebrow>
            <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-8">
              {S12_FACTS.map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-[#e62429] text-5xl sm:text-6xl leading-none">{n}</p>
                  <p className="mt-2 text-sm sm:text-base text-neutral-400">{l}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-sm uppercase tracking-[0.2em] text-neutral-500 font-bold">Round Robin → Super 8 League → Eliminators → Final</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <img src={`${AT}/arch-11.jpg`} alt="AT20 Season 12 — 2026" loading="lazy" className="w-full h-auto border border-white/10" />
          </Reveal>
        </div>
      </section>

      {/* ENDING */}
      <section data-testid="at20-ending" className="relative isolate min-h-[85svh] flex items-end overflow-hidden border-b border-white/10">
        <img src={`${AT}/champions.jpg`} alt="AT20 Champions" className="absolute inset-0 z-0 h-full w-full object-cover object-[center_30%]" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-[#0a0a0a]/20" />
        <div className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-white/70 font-bold">AT20 · Season 12 · Architect's T20 Championship Trophy</p>
            <p className="mt-6 font-display uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.85] drop-shadow-lg">
              A tournament for architects.
            </p>
            <p className="mt-2 font-display uppercase text-[#e62429] text-5xl sm:text-7xl lg:text-8xl leading-[0.85]">Still theirs.</p>
          </Reveal>
        </div>
      </section>

      <ChapterNav currentKey="at20" />

      {/* Route back */}
      <section className="py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-14">
          <Link to="/work" data-testid="at20-back-work-bottom" className="group inline-flex items-center gap-3 font-display uppercase text-2xl sm:text-3xl text-white">
            <ArrowLeft className="h-6 w-6 text-[#e62429] group-hover:-translate-x-1 transition-transform" />
            Back to Work
          </Link>
          <Link to="/#gateways" data-testid="at20-back-explore-bottom" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}
